<template>
  <UApp :scroll-body="false">
    <div>
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>

      <ClientOnly>
        <ToastContainer />

        <StepUpModal />
      </ClientOnly>

      <AppAnnouncer />
    </div>
  </UApp>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useHead, useRoute } from '#imports'
import { ClientOnly, NuxtLayout, NuxtPage } from '#components'
import UApp from '@nuxt/ui/components/App.vue'
import { useI18n } from 'vue-i18n'
import { useCartActions } from '~/composables/useCartActions'
import { useCartStore } from '~/stores/useCartStore'
import AppAnnouncer from '~/components/shared/AppAnnouncer.vue'
import { Normalization } from '~/utils/normalization'
import { useLogger } from '~/composables/useLogger'
import { useIdentity } from '~/composables/useIdentity'
import { useGuestToken } from '~/composables/useGuestToken'
import ToastContainer from '~/components/ToastContainer.client.vue'
import StepUpModal from '~/components/StepUpModal.client.vue'

const logger = useLogger('App')
const { locale } = useI18n()
const route = useRoute()

// Set HTML and body classes; lang is reactive so it stays correct across locale switches
useHead(() => ({
  bodyAttrs: {
    class: 'h-full'
  },
  htmlAttrs: {
    class: 'h-full bg-surface-canvas dark:bg-surface-level-900',
    lang: locale.value
  }
}))

const cartStore = useCartStore()
const { fetchCart } = useCartActions()
const { getGuestToken } = useGuestToken()
const { isAuthenticated } = useIdentity()

const refreshCart = async () => {
  if (import.meta.client) {
    const guestToken = getGuestToken()

    logger.debug('refreshCart', { isAuthenticated: isAuthenticated.value })

    await fetchCart(guestToken)

    const slug = Normalization.trimmedStringValue(route.params['slug'])
    if (route.path.startsWith('/store/') && slug !== null) {
      cartStore.setCurrentStore(slug)
    }
  }
}

watch(
  () => isAuthenticated.value,
  async (newValue, oldValue) => {
    // Only refresh if auth state actually changed (not on initial mount)
    if (oldValue !== undefined && newValue !== oldValue) {
      logger.debug('watchAuthState', { from: oldValue, to: newValue })
      await refreshCart()
    }
  }
)

onMounted(refreshCart)
</script>
